import React, { useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowRight } from 'lucide-react';
import { FadeIn } from '@/components/Animations';
import ServiceCard from '@/components/ServiceCard';
import { useTranslation } from 'react-i18next';
import { useServices, Service } from '@/hooks/useServices';
import { useIsMobile } from '@/hooks/use-mobile';
import { ServiceCardProps } from '@/components/ServiceCard';
import { BusinessByBadge } from '@communitly/shared';

interface AllServicesSectionProps {
  prepareServiceForCard: (service: BusinessByBadge) => ServiceCardProps;
}

// Convert Service type to BusinessByBadge type for prepareServiceForCard
const convertServiceToBusinessByBadge = (service: Service): BusinessByBadge => {
  return {
    id: service.id,
    name: service.name,
    slug: service.slug || '',
    company_logo: service.companyLogo,
    company_banner_url: service.companyBannerUrl,
    city: service.address?.city || '',
    postal_code: service.address?.postalCode || '',
    opening_hours: service.opening_hours,
    is_active: service.is_active || true,
    category_id: service.category || '',
    category_name: service.categoryName || service.category || '',
    category_slug: service.category || '',
    category_icon: undefined,
    community_id: '',
    avg_rating: service.rating || 0,
    review_count: service.reviewCount || 0,
    has_discount: !!service.discount,
    discount_id: service.discount?.id,
    discount_percentage: service.discount?.isPercentage ? service.discount.amount : undefined,
    discount_amount: !service.discount?.isPercentage ? service.discount?.amount : undefined,
    discount_applies_to: service.discount?.appliesToAll ? 'all' : 'specific',
    discount_type: service.discount?.type,
    discount_description: service.discount?.description
  };
};

export const AllServicesSection: React.FC<AllServicesSectionProps> = ({
  prepareServiceForCard
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error
  } = useServices(undefined, undefined, {
    infinite: true,
    pageSize: 3  // Reduced to 3 to make infinite scroll more visible
  });

  // Get all services from pages
  const allServices = data?.pages.flatMap(page => page.services) || [];

  // Intersection Observer callback
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
      console.log('Infinite scroll triggered - fetching next page');
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Set up intersection observer
  useEffect(() => {
    const currentLoadMoreRef = loadMoreRef.current;
    
    if (currentLoadMoreRef) {
      observerRef.current = new IntersectionObserver(handleIntersection, {
        rootMargin: '100px', // Trigger 100px before the element comes into view
        threshold: 0.1
      });
      
      observerRef.current.observe(currentLoadMoreRef);
    }

    return () => {
      if (observerRef.current && currentLoadMoreRef) {
        observerRef.current.unobserve(currentLoadMoreRef);
      }
    };
  }, [handleIntersection]);

  // Loading skeleton for responsive layout
  const LoadingSkeleton = () => {
    if (isMobile) {
      // Mobile: Row layout skeletons
      return (
        <>
          {Array(3).fill(0).map((_, index) => (
            <div key={index} className="rounded-2xl overflow-hidden bg-white shadow-md border border-gray-100 animate-pulse">
              <div className="flex flex-col">
                <div className="aspect-[4/2.5] w-full bg-muted"></div>
                <div className="flex-1 p-4 space-y-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="h-6 bg-muted rounded w-20"></div>
                    <div className="h-5 bg-muted rounded w-16"></div>
                  </div>
                  <div className="h-6 bg-muted rounded w-3/4"></div>
                  <div className="h-5 bg-muted rounded w-1/2"></div>
                  <div className="h-6 bg-muted rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </>
      );
    }
    
    // Desktop: Grid layout skeletons
    return (
      <>
        {Array(6).fill(0).map((_, index) => (
          <div key={index} className="bg-card rounded-2xl overflow-hidden shadow-sm border border-border animate-pulse">
            <div className="h-48 bg-muted"></div>
            <div className="p-5 space-y-3">
              <div className="h-5 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="h-4 bg-muted rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </>
    );
  };

  // Error state
  if (isError) {
    return (
      <section className="py-8 lg:py-12 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-muted-foreground mb-4">
            {t('home.services.error', { defaultValue: 'Unable to load services. Please try again.' })}
          </p>
          <Button 
            onClick={() => window.location.reload()}
            variant="outline"
          >
            {t('common.retry', { defaultValue: 'Retry' })}
          </Button>
        </div>
      </section>
    );
  }

  // Don't render if no services are available and not loading
  if (!isLoading && allServices.length === 0) {
    return null;
  }

  return (
    <section className="py-3 lg:py-12 px-4 md:px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="flex justify-between items-center mb-6 lg:mb-8">
          <FadeIn direction="up">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
              {t('home.all_services.title', { defaultValue: 'All Services' })}
            </h2>
          </FadeIn>
        </div>

        {/* Services List */}
        <div className={isMobile ? "space-y-4" : "grid grid-cols-3 gap-6 md:gap-8"}>
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {allServices.map((service: Service, index: number) => (
                <FadeIn key={service.id} direction="up" delay={index * 0.05}>
                  <ServiceCard
                    {...prepareServiceForCard(convertServiceToBusinessByBadge(service))}
                    layout={isMobile ? "row" : "grid"}
                    isMobile={isMobile}
                    delay={0} // Remove stagger delay for infinite scroll
                  />
                </FadeIn>
              ))}
            </>
          )}

          {/* Show skeleton cards when fetching next page */}
          {isFetchingNextPage && (
            isMobile ? (
              // Mobile: Row layout skeletons
              <div className="space-y-4 mt-4">
                {Array(3).fill(0).map((_, index) => (
                  <div key={`skeleton-${index}`} className="rounded-2xl overflow-hidden bg-white shadow-md border border-gray-100 animate-pulse">
                    <div className="flex flex-col">
                      <div className="aspect-[4/2.5] w-full bg-muted"></div>
                      <div className="flex-1 p-4 space-y-3">
                        <div className="flex justify-between items-start gap-3">
                          <div className="h-6 bg-muted rounded w-20"></div>
                          <div className="h-5 bg-muted rounded w-16"></div>
                        </div>
                        <div className="h-6 bg-muted rounded w-3/4"></div>
                        <div className="h-5 bg-muted rounded w-1/2"></div>
                        <div className="h-6 bg-muted rounded w-24"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop: Grid layout skeletons
              <>
                {Array(3).fill(0).map((_, index) => (
                  <div key={`skeleton-${index}`} className="bg-card rounded-2xl overflow-hidden shadow-sm border border-border animate-pulse">
                    <div className="h-48 bg-muted"></div>
                    <div className="p-5 space-y-3">
                      <div className="h-5 bg-muted rounded w-3/4"></div>
                      <div className="h-4 bg-muted rounded w-1/2"></div>
                      <div className="h-4 bg-muted rounded w-1/4"></div>
                    </div>
                  </div>
                ))}
              </>
            )
          )}

          {/* Infinite scroll trigger */}
          <div ref={loadMoreRef} className="py-4"></div>

          {/* Load more button (fallback for accessibility) */}
          {hasNextPage && !isFetchingNextPage && (
            <div className="flex justify-center pt-6">
              <Button 
                onClick={() => fetchNextPage()}
                variant="outline"
                className="group"
                disabled={isFetchingNextPage}
              >
                {t('home.services.load_more', { defaultValue: 'Load More Services' })}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};